import { useCallback, useMemo } from "react";

import { create } from "@bufbuild/protobuf";
import { DownloadSimpleIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";

import Button, { ButtonVariant } from "@gx/dls/buttons/Button";
import Chip, { ChipSize } from "@gx/dls/chips/Chip";
import FlexWrapper from "@gx/dls/containers/FlexWrapper";
import InfiniteTable, { ColumnDef } from "@gx/dls/table/InfiniteTable";
import Text, { TextVariant } from "@gx/dls/text/Text";
import TextShimmer from "@gx/dls/text/TextShimmer";
import {
  DownloadSourceArtifactRequestSchema,
  ListSourceArtifactsRequestSchema,
} from "@gx/ts-contracts/services/source/api/source_artifact_pb";
import { SourceArtifactResource } from "@gx/ts-contracts/services/source/resources/source_artifact_pb";

import EmptyLayout from "@/layouts/EmptyLayout";

import {
  SOURCE_ARTIFACT_CONTENT_TYPE_TO_NAME_MAP,
  SOURCE_ARTIFACT_TYPE_TO_NAME_MAP,
} from "@/pages/sources/components/edit/tabs/artifacts/constants";

import { useDownloadSourceArtifactMutation } from "@/api/mutations/source-artifacts";
import { useListSourceArtifactsInfiniteQuery } from "@/api/queries/source-artifacts";

import { useDownloadFile } from "@/hooks/useDownloadFile";

import { convertProtobufTimestampToDate, formatDateTime } from "@/utils/datetimes";

interface SourceArtifactsTableCellProps {
  artifact: SourceArtifactResource;
}

const DownloadCell = ({ artifact }: SourceArtifactsTableCellProps) => {
  const { mutateAsync: getDownloadUrl, isPending } = useDownloadSourceArtifactMutation();

  const handleDownload = useDownloadFile({
    getUrl: async () => {
      const result = await getDownloadUrl(
        create(DownloadSourceArtifactRequestSchema, {
          artifactId: artifact.id,
        }),
      );
      return result?.downloadUri ?? "";
    },
    filename: artifact.id,
  });

  return (
    <Button
      label="Download"
      variant={ButtonVariant.SECONDARY}
      icon={DownloadSimpleIcon}
      onClick={(e) => {
        e.stopPropagation();
        void handleDownload();
      }}
      isDisabled={isPending}
    />
  );
};

const DownloadCellLoading = () => {
  return <TextShimmer height={16} width="80%" />;
};

const ArtifactTypeCell = ({ artifact }: SourceArtifactsTableCellProps) => {
  return (
    <FlexWrapper>
      <Chip label={SOURCE_ARTIFACT_TYPE_TO_NAME_MAP[artifact.type]} size={ChipSize.LARGE} />
    </FlexWrapper>
  );
};

const ArtifactTypeCellLoading = () => {
  return <TextShimmer height={16} width="80%" />;
};

const ContentTypeCell = ({ artifact }: SourceArtifactsTableCellProps) => {
  return <Text>{SOURCE_ARTIFACT_CONTENT_TYPE_TO_NAME_MAP[artifact.contentType]}</Text>;
};

const ContentTypeCellLoading = () => {
  return <TextShimmer height={16} width="80%" />;
};

const CreatedAtCell = ({ artifact }: SourceArtifactsTableCellProps) => {
  return (
    <Text variant={TextVariant.SECONDARY}>
      {artifact.createdAt
        ? formatDateTime(convertProtobufTimestampToDate(artifact.createdAt).toISOString())
        : "—"}
    </Text>
  );
};

const CreatedAtCellLoading = () => {
  return <TextShimmer height={16} width={200} />;
};

const SOURCE_ARTIFACTS_TABLE_COLUMNS: ColumnDef<SourceArtifactResource>[] = [
  {
    id: "type",
    header: "Artifact",
    cellLoading: ArtifactTypeCellLoading,
    cell: ({ row }) => <ArtifactTypeCell artifact={row.original} />,
  },
  {
    id: "contentType",
    header: "Content type",
    size: 120,
    cellLoading: ContentTypeCellLoading,
    cell: ({ row }) => <ContentTypeCell artifact={row.original} />,
  },
  {
    id: "createdAt",
    header: "Created at",
    size: 180,
    cellLoading: CreatedAtCellLoading,
    cell: ({ row }) => <CreatedAtCell artifact={row.original} />,
  },
  {
    id: "download",
    header: "",
    size: 128,
    cellLoading: DownloadCellLoading,
    cell: ({ row }) => <DownloadCell artifact={row.original} />,
  },
];

interface SourceArtifactContentTableProps {
  sourceId: string;
}

const SourceArtifactContentTable = ({ sourceId }: SourceArtifactContentTableProps) => {
  const navigate = useNavigate();

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useListSourceArtifactsInfiniteQuery({
    input: create(ListSourceArtifactsRequestSchema, {
      sourceId: sourceId,
    }),
  });

  const handleRowClick = useCallback(
    (row: { original: SourceArtifactResource }) => {
      void navigate({
        to: ".",
        search: (prev) => ({ ...prev, sourceArtifactId: row.original.id }),
      });
    },
    [navigate],
  );

  const data = useMemo(() => {
    if (!infiniteData) return [];
    return infiniteData.pages.flatMap((page) => page.artifacts);
  }, [infiniteData]);

  return (
    <InfiniteTable<SourceArtifactResource>
      columns={SOURCE_ARTIFACTS_TABLE_COLUMNS}
      data={data}
      getRowId={(row) => row.id}
      fillWidth
      fillHeight
      fetchNextPage={fetchNextPage} // eslint-disable-line @typescript-eslint/no-misused-promises
      hasNextPage={hasNextPage ?? false}
      isFetchingNextPage={isFetchingNextPage}
      onRowClick={handleRowClick}
      contentWhenEmpty={
        <EmptyLayout title="No artifacts yet" description="Source artifacts will appear here" />
      }
      isLoading={isLoading && !infiniteData}
      loadingRowCount={25}
    />
  );
};

export default SourceArtifactContentTable;
